import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Architecture as FloorPlanIcon,
  Home as RoomIcon,
  AspectRatio as SizeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as FlowIcon,
  Star as FeatureIcon,
} from '@mui/icons-material';
import { FloorPlanResult } from '../../../types/property';

interface FloorPlanTabProps {
  data: FloorPlanResult;
  loading?: boolean;
  error?: string;
}

const FloorPlanTab: React.FC<FloorPlanTabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading floor plan analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No floor plan analysis available
      </Alert>
    );
  }

  const { floorPlanAnalysis, confidence, dataQuality, missingFields, suggestions } = data;

  const getConfidenceColor = (conf: number) => {
    if (conf >= 0.8) return 'success';
    if (conf >= 0.6) return 'warning';
    return 'error';
  };

  const getFlowColor = (flow: string) => {
    if (flow === 'excellent') return 'success';
    if (flow === 'good') return 'info';
    if (flow === 'fair') return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Analysis Quality Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FloorPlanIcon color="primary" />
              Floor Plan Analysis
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<CheckIcon />}
                label={`${Math.round(confidence * 100)}% Confidence`}
                color={getConfidenceColor(confidence)}
              />
              <Chip
                label={`${dataQuality} Quality`}
                color={dataQuality === 'high' ? 'success' : dataQuality === 'medium' ? 'warning' : 'error'}
                variant="outlined"
              />
            </Box>
          </Box>

          {missingFields.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Missing data: {missingFields.join(', ')}
            </Alert>
          )}

          {suggestions.length > 0 && (
            <Alert severity="warning">
              Suggestions: {suggestions.join('; ')}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Square Footage Verification */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SizeIcon color="primary" />
                Square Footage Verification
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {floorPlanAnalysis.squareFootageVerification.listedSqFt.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Listed Sq Ft
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                  <Typography variant="h5" fontWeight="bold" color="secondary">
                    {floorPlanAnalysis.squareFootageVerification.calculatedSqFt.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Calculated Sq Ft
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    color={floorPlanAnalysis.squareFootageVerification.difference === 0 ? 'success.main' : 'warning.main'}
                  >
                    {floorPlanAnalysis.squareFootageVerification.difference > 0 ? '+' : ''}{floorPlanAnalysis.squareFootageVerification.difference.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Difference
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                  <Typography variant="h5" fontWeight="bold">
                    <Chip
                      label={`${Math.round(floorPlanAnalysis.squareFootageVerification.confidence * 100)}%`}
                      color={getConfidenceColor(floorPlanAnalysis.squareFootageVerification.confidence)}
                    />
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Verification Confidence
                  </Typography>
                </Box>
              </Box>

              {floorPlanAnalysis.squareFootageVerification.recommendations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recommendations
                  </Typography>
                  <List dense>
                    {floorPlanAnalysis.squareFootageVerification.recommendations.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={rec} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Room Flow Assessment */}
        <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlowIcon color="primary" />
                Layout & Flow Assessment
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Overall Flow</Typography>
                <Chip 
                  label={floorPlanAnalysis.roomFlowAssessment.overallFlow} 
                  color={getFlowColor(floorPlanAnalysis.roomFlowAssessment.overallFlow)}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>

              {floorPlanAnalysis.roomFlowAssessment.trafficPatterns.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Traffic Patterns
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {floorPlanAnalysis.roomFlowAssessment.trafficPatterns.map((pattern, index) => (
                      <Chip key={index} label={pattern} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {floorPlanAnalysis.roomFlowAssessment.optimizationSuggestions.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Optimization Suggestions
                  </Typography>
                  <List dense>
                    {floorPlanAnalysis.roomFlowAssessment.optimizationSuggestions.map((suggestion, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <WarningIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={suggestion} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Feature Identification */}
        <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FeatureIcon color="primary" />
                Feature Analysis
              </Typography>

              {floorPlanAnalysis.featureIdentification.premiumFeatures.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                    Premium Features
                  </Typography>
                  <List dense>
                    {floorPlanAnalysis.featureIdentification.premiumFeatures.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <FeatureIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {floorPlanAnalysis.featureIdentification.uniqueFeatures.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'info.main' }}>
                    Unique Features
                  </Typography>
                  <List dense>
                    {floorPlanAnalysis.featureIdentification.uniqueFeatures.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {floorPlanAnalysis.featureIdentification.valueAdditions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                    Value Additions
                  </Typography>
                  <List dense>
                    {floorPlanAnalysis.featureIdentification.valueAdditions.map((addition, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={addition} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {floorPlanAnalysis.featureIdentification.premiumFeatures.length === 0 && 
               floorPlanAnalysis.featureIdentification.uniqueFeatures.length === 0 && 
               floorPlanAnalysis.featureIdentification.valueAdditions.length === 0 && (
                <Alert severity="info">
                  No specific premium features or unique characteristics identified in the floor plan analysis.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Level Details */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <RoomIcon color="primary" />
                Level Details
              </Typography>

              {floorPlanAnalysis.levels.map((level, levelIndex) => (
                <Box key={levelIndex} sx={{ mb: levelIndex < floorPlanAnalysis.levels.length - 1 ? 3 : 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Level {level.level}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Square Footage</Typography>
                      <Typography variant="h6">{level.squareFootage.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Verified Sq Ft</Typography>
                      <Typography variant="h6">{level.verifiedSquareFootage.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Confidence</Typography>
                      <Chip
                        label={`${Math.round(level.confidence * 100)}%`}
                        color={getConfidenceColor(level.confidence)}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Total Rooms</Typography>
                      <Typography variant="h6">{level.rooms.length}</Typography>
                    </Box>
                  </Box>

                  {/* Layout Assessment */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Layout Assessment</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">Open Concept</Typography>
                        <Chip 
                          label={level.layout.openConcept ? 'Yes' : 'No'} 
                          color={level.layout.openConcept ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">Flow</Typography>
                        <Chip 
                          label={level.layout.flow} 
                          color={getFlowColor(level.layout.flow)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">Accessibility</Typography>
                        <Chip 
                          label={level.layout.accessibility} 
                          color={getFlowColor(level.layout.accessibility)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">Entertainment Flow</Typography>
                        <Chip 
                          label={level.layout.entertainmentFlow} 
                          color={getFlowColor(level.layout.entertainmentFlow)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Room Breakdown */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Room Breakdown</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {level.rooms.map((room, roomIndex) => (
                        <Box sx={{ flex: 1, minWidth: '25%' }} key={roomIndex}>
                          <Card variant="outlined" sx={{ p: 1 }}>
                            <Typography variant="body2" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                              {room.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {room.location}
                            </Typography>
                            {room.premiumFeatures.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                {room.premiumFeatures.map((feature, featureIndex) => (
                                  <Chip 
                                    key={featureIndex}
                                    label={feature} 
                                    size="small" 
                                    color="primary" 
                                    sx={{ mr: 0.5, mb: 0.5, fontSize: '0.6rem', height: 20 }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Card>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {levelIndex < floorPlanAnalysis.levels.length - 1 && <Divider />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default FloorPlanTab;